
import React, { useState } from 'react';
import { UserProfile, ContentItem, Language } from '../types';
// Fix: removed non-existent generateJoke import
import { generateQuote, evaluateUserJoke } from '../services/geminiService';
import { Heart, Smile, BookOpen, Share2, Frown, Meh, Laugh, ThumbsUp, Sparkles, Send, Loader2, Bookmark, Check, Twitter, Send as Telegram, Instagram, PenTool, Quote, Star } from 'lucide-react';
import { TRANSLATIONS } from '../translations';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

type Tab = 'poetry' | 'wisdom' | 'fun' | 'contest' | 'favorites';

const CompanionHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<Tab>('poetry');
  const [loading, setLoading] = useState(false);
  const [currentContent, setCurrentContent] = useState<ContentItem | null>(null);
  const [showCopied, setShowCopied] = useState(false);
  
  const t = TRANSLATIONS[user.language] || TRANSLATIONS[Language.KURDISH];

  // Contest State
  const [userJoke, setUserJoke] = useState('');
  const [contestResult, setContestResult] = useState<{score: number, comment: string} | null>(null);

  const wisdomCategories = [
    { id: 'motivational', label: t.motivational, emoji: '💪' },
    { id: 'philosophical', label: t.philosophical, emoji: '🤔' },
    { id: 'stoic', label: user.language === Language.PERSIAN ? 'رواقی‌گری' : 'فەلسەفەی ستۆیک', emoji: '🏛️' },
    { id: 'daily_wisdom', label: user.language === Language.PERSIAN ? 'پند روز' : 'پەندی پێشینان', emoji: '📜' },
  ];

  const poetryCategories = [
    { id: 'classic', label: t.classic_poetry, emoji: '🏺' },
    { id: 'modern', label: t.modern_poetry, emoji: '🏙️' },
    { id: 'mystic', label: t.mystic_poetry, emoji: '🕯️' },
    { id: 'haiku', label: t.haiku_poetry, emoji: '🌸' },
  ];

  const jokeCategories = [
    { id: 'family', label: user.language === Language.PERSIAN ? 'خانوادگی' : 'خێزانی', emoji: '👨‍👩‍👧‍👦' },
    { id: 'clever', label: user.language === Language.PERSIAN ? 'هوشمندانه' : 'زیرەکانە', emoji: '🤓' },
    { id: 'work', label: user.language === Language.PERSIAN ? 'کاری' : 'پیشەیی', emoji: '💼' },
    { id: 'dark', label: user.language === Language.PERSIAN ? 'طنز تلخ' : 'تەنزی تاڵ', emoji: '🌑' },
  ];

  const handleGenerateContent = async (category: string, type: 'quote' | 'joke' | 'poetry') => {
    setLoading(true);
    setCurrentContent(null);
    try {
      const { text, author } = await generateQuote(user, category);
      const newItem: ContentItem = {
        id: Date.now().toString(),
        type: type === 'joke' ? 'joke' : 'quote',
        text,
        author,
        category,
        timestamp: Date.now()
      };
      setCurrentContent(newItem);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleJokeContest = async () => {
      if (!userJoke.trim()) return;
      setLoading(true);
      try {
          const res = await evaluateUserJoke(user, userJoke);
          setContestResult(res);
      } catch (e) {
          console.error(e);
      } finally {
          setLoading(false);
      }
  };

  const handleReaction = (reaction: 'like' | 'dislike' | 'love') => {
    if (!currentContent) return;
    const updatedPreferences = { ...user.contentPreferences };
    if (reaction === 'love' || reaction === 'like') {
      if (!updatedPreferences.likedCategories.includes(currentContent.category)) {
        updatedPreferences.likedCategories.push(currentContent.category);
      }
    }
    if (reaction === 'love') {
      const exists = updatedPreferences.favorites.find(f => f.text === currentContent.text);
      if (!exists) {
        updatedPreferences.favorites.push({ ...currentContent, rating: 'love' });
      }
    }
    onUpdateUser({ ...user, contentPreferences: updatedPreferences });
    setCurrentContent({ ...currentContent, rating: reaction });
  };

  const handleShare = async () => {
    if (!currentContent) return;
    const textToShare = `"${currentContent.text}" ${currentContent.author ? `- ${currentContent.author}` : ''} \n\n✨ ${t.appName} ✨`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: t.appName,
          text: textToShare,
          url: window.location.href
        });
      } catch (err) { /* silent */ }
    } else {
      navigator.clipboard.writeText(textToShare);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    }
  };

  const TabButton = ({ id, label, icon: Icon }: any) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-6 py-3.5 rounded-2xl transition-all font-black text-sm whitespace-nowrap
        ${activeTab === id 
          ? 'bg-slate-900 text-white shadow-xl scale-105' 
          : 'bg-white text-slate-500 hover:bg-slate-50 border border-slate-100 shadow-sm'
        }
      `}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="mb-8 text-right">
           <h2 className="text-3xl font-black text-slate-800 flex items-center justify-end gap-3">
             {t.companion}
             <div className="p-3 bg-amber-100 text-amber-600 rounded-2xl shadow-sm"><BookOpen className="w-6 h-6" /></div>
           </h2>
           <p className="text-slate-500 text-sm mt-2 font-medium">{user.language === Language.PERSIAN ? 'دنیای ادبیات، خرد و سرگرمی' : 'جیهانی ئەدەبیات، حیکمەت و کات بەسەربردن'}</p>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-6 no-scrollbar mb-4">
        <TabButton id="poetry" label={t.poetry_literature} icon={PenTool} />
        <TabButton id="wisdom" label={t.wisdom_day} icon={Quote} />
        <TabButton id="fun" label={t.fun_time} icon={Smile} />
        <TabButton id="contest" label={t.joke_contest} icon={Laugh} />
        <TabButton id="favorites" label={t.favorites} icon={Heart} />
      </div>

      <div className="flex-1 overflow-y-auto pr-1">
        {(activeTab === 'poetry' || activeTab === 'wisdom' || activeTab === 'fun') && (
          <div className="space-y-8">
            <div className="flex flex-wrap gap-2 justify-end">
              {(activeTab === 'poetry' ? poetryCategories : activeTab === 'wisdom' ? wisdomCategories : jokeCategories).map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleGenerateContent(cat.id, activeTab === 'fun' ? 'joke' : activeTab === 'poetry' ? 'poetry' : 'quote')}
                  className="px-5 py-3 bg-white border border-slate-200 rounded-2xl text-slate-700 hover:border-indigo-400 hover:bg-indigo-50 transition-all text-sm font-bold flex items-center gap-2 shadow-sm hover:shadow-md"
                >
                  <span className="text-lg">{cat.emoji}</span>
                  {cat.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 text-slate-400 bg-white/50 rounded-[3rem] border-2 border-dashed border-slate-200">
                <Loader2 className="w-12 h-12 animate-spin mb-4 text-indigo-500" />
                <p className="font-black animate-pulse">{t.generating_content}</p>
              </div>
            ) : currentContent ? (
              <div className="bg-white rounded-[3rem] p-10 border border-slate-100 shadow-2xl relative overflow-hidden animate-slide-up">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
                <div className="relative z-10 text-center">
                  <div className="inline-flex p-4 rounded-3xl bg-slate-50 mb-8 shadow-inner">
                     {activeTab === 'poetry' ? <PenTool className="w-8 h-8 text-rose-500" /> : activeTab === 'wisdom' ? <Quote className="w-8 h-8 text-indigo-600" /> : <Smile className="w-8 h-8 text-amber-500" />}
                  </div>
                  
                  <div className={`text-2xl md:text-3xl font-serif font-black text-slate-800 leading-[1.8] mb-8 text-center whitespace-pre-line ${activeTab === 'poetry' ? 'tracking-wide' : ''}`}>
                    {currentContent.text}
                  </div>
                  
                  {currentContent.author && (
                    <div className="flex items-center justify-center gap-2 mb-10">
                        <div className="w-8 h-px bg-slate-200"></div>
                        <cite className="text-slate-500 font-bold not-italic text-lg">{currentContent.author}</cite>
                        <div className="w-8 h-px bg-slate-200"></div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-center gap-4 pt-8 border-t border-slate-50">
                    <button 
                      onClick={() => handleReaction('dislike')} 
                      aria-label="dislike"
                      className={`p-4 rounded-2xl transition-all ${currentContent.rating === 'dislike' ? 'bg-slate-200 text-slate-600' : 'bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-500'}`}
                    >
                      <Meh className="w-6 h-6" />
                    </button>
                    <button 
                      onClick={() => handleReaction('like')} 
                      aria-label="like"
                      className={`p-4 rounded-2xl transition-all ${currentContent.rating === 'like' ? 'bg-amber-100 text-amber-600 shadow-lg' : 'bg-slate-50 text-slate-400 hover:bg-amber-50 hover:text-amber-500'}`}
                    >
                      <Laugh className="w-6 h-6" />
                    </button>
                    <button 
                      onClick={() => handleReaction('love')} 
                      aria-label="love"
                      className={`p-4 rounded-2xl transition-all ${currentContent.rating === 'love' ? 'bg-rose-100 text-rose-600 shadow-xl' : 'bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-500'}`}
                    >
                      <Heart className={`w-6 h-6 ${currentContent.rating === 'love' ? 'fill-current' : ''}`} />
                    </button>
                    
                    <div className="w-px h-10 bg-slate-100 mx-2"></div>
                    
                    <button 
                        onClick={handleShare} 
                        aria-label="share"
                        className={`p-4 rounded-2xl transition-all bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200`}
                    >
                        {showCopied ? <Check className="w-6 h-6" /> : <Share2 className="w-6 h-6" />}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-24 text-slate-400 bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200">
                <Sparkles className="w-16 h-16 mx-auto mb-6 opacity-20" />
                <p className="font-black text-xl">{t.select_topic_smile}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contest' && (
          <div className="bg-white rounded-[3rem] p-8 border border-slate-100 shadow-xl animate-fade-in">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-amber-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Laugh className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-2xl font-black text-slate-800 mb-2">{t.joke_contest} 🎙️</h3>
              <p className="text-slate-500 font-medium">{t.joke_judge}</p>
            </div>
            <div className="mb-8">
              <textarea 
                value={userJoke} 
                onChange={(e) => setUserJoke(e.target.value)} 
                placeholder="..." 
                className="w-full h-40 p-6 rounded-[2rem] border-2 border-slate-100 focus:ring-4 focus:ring-indigo-50 focus:border-indigo-300 outline-none resize-none bg-slate-50 text-slate-800 text-lg shadow-inner transition-all" 
              />
              <button 
                onClick={handleJokeContest} 
                disabled={loading || !userJoke.trim()} 
                className="w-full mt-6 py-5 bg-indigo-600 text-white rounded-2xl font-black text-lg hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-2xl shadow-indigo-500/20"
              >
                {loading ? <Loader2 className="animate-spin w-6 h-6" /> : <Send className="w-6 h-6 rtl:-rotate-90" />}
                {t.send_to_judge}
              </button>
            </div>

            {contestResult && (
                <div className="p-8 rounded-[2rem] bg-indigo-50 border border-indigo-100 animate-slide-up">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <Star className="w-6 h-6 text-amber-500 fill-current" />
                            <span className="text-xl font-black text-slate-800">امتیاز داور: {contestResult.score}/10</span>
                        </div>
                    </div>
                    <p className="text-lg text-slate-700 italic font-medium leading-relaxed">"{contestResult.comment}"</p>
                </div>
            )}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
            {user.contentPreferences.favorites.length === 0 ? (
              <div className="col-span-full text-center py-24 text-slate-400 bg-slate-50 rounded-[3rem] border-2 border-dashed border-slate-200">
                <Heart className="w-16 h-16 mx-auto mb-6 opacity-20" />
                <p className="font-black text-xl">{t.no_favorites}</p>
              </div>
            ) : (
              user.contentPreferences.favorites.map((item) => (
                <div key={item.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-md relative group hover:border-rose-300 transition-all hover:-translate-y-1">
                   <div className="absolute top-6 left-6 text-rose-500"><Bookmark className="w-6 h-6 fill-current" /></div>
                   <p className="text-slate-800 font-serif font-black leading-relaxed mb-6 text-xl">{item.text}</p>
                   <div className="flex justify-between items-center text-[10px] text-slate-400 font-black uppercase tracking-widest pt-6 border-t border-slate-50">
                     <span className="flex items-center gap-2"><PenTool className="w-3 h-3" /> {item.author || (user.language === Language.PERSIAN ? 'ناشناس' : 'بێناو')}</span>
                     <span className="bg-indigo-50 text-indigo-500 px-3 py-1 rounded-full font-black uppercase">{item.category}</span>
                   </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanionHub;
